import React from 'react';
import styled from 'styled-components';
import { ShieldCheck, Lock, Zap, Award, Clock, Star } from 'lucide-react';

const trustItems = [
  { icon: ShieldCheck, title: 'Fortified Security', subtitle: 'SSL encrypted & GDPR compliant' },
  { icon: Lock, title: 'Privacy Protected', subtitle: 'Your data stays yours, always' },
  { icon: Zap, title: 'Built for Reliability', subtitle: 'Reliable & always available' },
  { icon: Award, title: 'Proven Excellence', subtitle: 'Trusted by professionals' },
  { icon: Clock, title: 'Expert Support', subtitle: 'We are here to help' },
  { icon: Star, title: 'Loved by Photographers', subtitle: '1000+ verified reviews' },
];

const TrustSection = () => (
  <Section>
    <Heading>Trusted by photographers worldwide</Heading>
    <Timeline>
      {trustItems.map(({ icon: Icon, title, subtitle }, index) => (
        <Item key={title}>
          {index < trustItems.length - 1 && <Connector />}
          <IconWrap>
            <Icon size={20} strokeWidth={1.8} />
          </IconWrap>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
        </Item>
      ))}
    </Timeline>
  </Section>
);

export default TrustSection;

const Section = styled.section`
  width: 100%;
  padding: 40px 20px 50px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #ffffff;
`;

const Heading = styled.div`
  font-family: Urbanist, 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #6b6b6b;
  margin-bottom: 30px;
  text-align: center;
`;

const Timeline = styled.div`
  --icon: 56px;
  --icon-glyph: 22px;
  --ring: 8px;

  width: 90%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  row-gap: 36px;

  @media (min-width: 1280px) {
    --icon: 60px;
    --icon-glyph: 24px;
  }

  @media (min-width: 1536px) {
    --icon: 55px;
    --icon-glyph: 20px;
    --ring: 8px;
  }

  @media (max-width: 1030px) {
    --icon: 52px;
    --icon-glyph: 22px;
    --ring: 7px;
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 500px) {
    --icon: 44px;
    --icon-glyph: 18px;
    --ring: 6px;
    width: 100%;
    grid-template-columns: repeat(2, 1fr);
    row-gap: 28px;
  }
`;

const Connector = styled.span`
  position: absolute;
  top: calc(var(--icon) / 2 + var(--ring));
  left: calc(50% + var(--icon) / 2 + var(--ring));
  width: calc(100% - var(--icon) - var(--ring) * 2);
  height: 1px;
  background: rgba(201, 77, 170, 0.3);
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(201, 77, 170, 0.45);
    transform: translate(-50%, -50%);
  }
`;

const IconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: var(--icon);
  height: var(--icon);
  margin: var(--ring) var(--ring) calc(var(--ring) + 14px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.18) 0%, rgba(201, 77, 170, 0.18) 100%);
  border: 1px solid rgba(201, 77, 170, 0.25);
  box-shadow: 0 0 0 var(--ring) rgba(201, 77, 170, 0.06), 0 6px 18px rgba(201, 77, 170, 0.12);
  transition: all 0.3s ease;

  svg {
    width: var(--icon-glyph);
    height: var(--icon-glyph);
  }
`;

const Item = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 8px;

  @media (max-width: 1030px) and (min-width: 501px) {
    &:nth-child(3n) ${Connector} {
      display: none;
    }
  }

  @media (max-width: 500px) {
    &:nth-child(2n) ${Connector} {
      display: none;
    }
  }

  &:hover ${IconWrap} {
    color: #fff;
    background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 120%);
    border-color: transparent;
    box-shadow: 0 0 0 var(--ring) rgba(201, 77, 170, 0.12), 0 10px 24px rgba(201, 77, 170, 0.25);
    transform: translateY(-3px);
  }
`;

const Title = styled.div`
  font-family: Urbanist, 'Poppins', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const Subtitle = styled.div`
  font-family: Urbanist, 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6b6b6b;
  max-width: 170px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;
